import { useRef, useSyncExternalStore } from "react";

export type OpsMediaOwner = "voice" | "meeting";
type DisarmVoice = () => void | Promise<void>;

/**
 * One page-local microphone arbiter. SpeechRecognition does not expose its
 * MediaStream, so the lease is the shared truth that lets meeting capture
 * disarm it before acquiring a real track.
 */
export class OpsMediaOwnerController {
  private currentOwner: OpsMediaOwner | null = null;
  private readonly listeners = new Set<() => void>();
  private disarmVoice: DisarmVoice | null = null;
  private meetingClaim: Promise<boolean> | null = null;

  get owner(): OpsMediaOwner | null { return this.currentOwner; }

  readonly getSnapshot = (): OpsMediaOwner | null => this.currentOwner;

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  claim(owner: OpsMediaOwner): boolean {
    if (this.currentOwner === owner) return true;
    if (this.currentOwner !== null) return false;
    this.currentOwner = owner;
    this.emit();
    return true;
  }

  release(owner: OpsMediaOwner): boolean {
    if (this.currentOwner !== owner) return false;
    this.currentOwner = null;
    this.emit();
    return true;
  }

  registerVoiceDisarm(disarm: DisarmVoice): () => void {
    this.disarmVoice = disarm;
    return () => {
      if (this.disarmVoice === disarm) this.disarmVoice = null;
    };
  }

  claimForMeeting(): Promise<boolean> {
    if (this.meetingClaim) return this.meetingClaim;
    this.meetingClaim = this.claimForMeetingOnce().finally(() => {
      this.meetingClaim = null;
    });
    return this.meetingClaim;
  }

  private async claimForMeetingOnce(): Promise<boolean> {
    if (this.currentOwner === "meeting") return true;
    if (this.currentOwner === "voice") {
      // WHY [BOL-ONE-MIC-OWNER-001]: meeting capture owns physical tracks and
      // must never race a hidden SpeechRecognition session. Its trusted click
      // first invokes the registered voice teardown, then claims only after
      // voice has synchronously released. Voice may never preempt a meeting.
      // Falsifier: both controls report active, or a meeting track opens while
      // the owner still says voice.
      if (!this.disarmVoice) return false;
      await this.disarmVoice();
    }
    return this.claim("meeting");
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

export function useOpsMediaOwner(): OpsMediaOwnerController {
  const controllerRef = useRef<OpsMediaOwnerController | null>(null);
  if (!controllerRef.current) controllerRef.current = new OpsMediaOwnerController();
  useSyncExternalStore(
    controllerRef.current.subscribe,
    controllerRef.current.getSnapshot,
    controllerRef.current.getSnapshot
  );
  return controllerRef.current;
}
